import { useCallback } from 'react';
import {
  fetchFileAccess,
  fetchPickerToken,
  GoogleIntegrationError,
} from '../../../api/google-integrations-client/google-integrations-client';
import { openDrivePicker } from '../../../helpers/google-picker/google-picker';
import { appConfig } from '../../../helpers/app-config/app-config';

/**
 * Outcome of driving the Google Picker for a specific expected file.
 *
 * - `granted`: the expected file is now accessible to Flora (the user picked it,
 *   or a re-check confirmed the pasted link resolved to a file they granted).
 * - `picked-different`: the user picked a file other than the expected one and
 *   the expected file is still inaccessible.
 * - `cancelled`: the user closed the picker without choosing a file.
 * - `reconnect-required`: the Google authorization has expired or been revoked.
 * - `error`: the picker token or picker itself failed to load.
 */
type GooglePickerOutcome = 'granted' | 'picked-different' | 'cancelled' | 'reconnect-required' | 'error';

interface UseGooglePickerResult {
  openPickerForFile: (
    organizationId: string,
    expectedFileId: string,
    fileType?: 'spreadsheet' | 'document' | 'presentation',
  ) => Promise<GooglePickerOutcome>;
}

/**
 * Map a pasted file type to the Drive views the picker should show. When the
 * type is unknown (e.g. a deep link with no type hint) all views are offered.
 *
 * @param {'spreadsheet' | 'document' | 'presentation' | undefined} fileType - The pasted file type, if known.
 * @returns {Array<'SPREADSHEETS' | 'DOCUMENTS' | 'PRESENTATIONS'>} The Drive view ids to show.
 */
function viewIdsForFileType(
  fileType?: 'spreadsheet' | 'document' | 'presentation',
): Array<'SPREADSHEETS' | 'DOCUMENTS' | 'PRESENTATIONS'> {
  switch (fileType) {
    case 'spreadsheet':
      return ['SPREADSHEETS'];
    case 'document':
      return ['DOCUMENTS'];
    case 'presentation':
      return ['PRESENTATIONS'];
    default:
      return ['SPREADSHEETS', 'DOCUMENTS', 'PRESENTATIONS'];
  }
}

/**
 * Shared driver for the Google Picker grant-access flow. Fetches a short-lived
 * picker token, opens the Drive picker scoped to the pasted file's type, and
 * resolves whether the expected file ended up accessible to Flora. Callers own
 * any side effects (caching, re-submitting a held message, navigation) and
 * branch on the returned outcome.
 *
 * @returns {UseGooglePickerResult} A single `openPickerForFile` driver.
 */
function useGooglePicker(): UseGooglePickerResult {
  const apiKey = appConfig.VITE_APP_GOOGLE_API_KEY;

  const openPickerForFile = useCallback(
    async (
      organizationId: string,
      expectedFileId: string,
      fileType?: 'spreadsheet' | 'document' | 'presentation',
    ): Promise<GooglePickerOutcome> => {
      if (!apiKey) {
        return 'error';
      }

      let token: { access_token: string; app_id: string };
      try {
        token = await fetchPickerToken(organizationId);
      } catch (error) {
        if (error instanceof GoogleIntegrationError && error.code === 'google_reconnect_required') {
          return 'reconnect-required';
        }
        return 'error';
      }

      let pickedFileId: string | null;
      try {
        pickedFileId = await openDrivePicker({
          apiKey,
          appId: token.app_id,
          accessToken: token.access_token,
          viewIds: viewIdsForFileType(fileType),
        });
      } catch {
        return 'error';
      }

      if (pickedFileId === null) {
        return 'cancelled';
      }

      if (pickedFileId === expectedFileId) {
        return 'granted';
      }

      // The user picked a different file than expected. The expected id may be
      // an alias for the same document, so re-check whether it became accessible.
      try {
        const accessible = await fetchFileAccess(organizationId, expectedFileId);
        if (accessible) {
          return 'granted';
        }
      } catch {
        // Fall through to the picked-different outcome.
      }
      return 'picked-different';
    },
    [],
  );

  return { openPickerForFile };
}

export { useGooglePicker };
